// ─── Finding a client in a list ──────────────────────────────────────────────
//
// Drea's instruction on 2026-09-12 was explicit: a real clients list and
// picker, "not search-per-keystroke against ad-hoc text". So the whole list is
// loaded once and filtered here, in the browser.
//
// That is not just simpler. A debounced server search has a latency between
// typing and seeing, gets slower exactly when the connection is bad, and needs
// a spinner state for something that should feel like scrolling. For a
// one-chair salon the list is a few hundred rows; filtering it is instant and
// works with no network at all.
//
// It stops being the right answer at some size — a chain with fifty thousand
// clients needs a server-side index. That is a real limit, written down rather
// than discovered.

export type ClientRecord = {
  id: string;
  fullName: string;
  phone: string | null;
  email?: string | null;
  /** Bookings that still stand. Declined and cancelled ones are not visits. */
  bookings: number;
  lastVisit?: string | null;
  nextVisit?: string | null;
  lastService?: string | null;
};

/** Digits only, so a number matches however it was punctuated. */
const digits = (value: string) => value.replace(/[^0-9]/g, "");

/**
 * The forms of a typed number worth looking for.
 *
 * A number saved as "+44 7700 900300" is stored as 447700900300, and somebody
 * searching types the national form 07700900300 — which is not a substring of
 * it, because of the leading zero. Without this, a client is unfindable purely
 * because of how their number happened to be entered.
 *
 * ── This is SEARCH, and search is not identity ──────────────────────────────
 *
 * `@nulife/people`'s `phoneKey` deliberately refuses to match on trailing
 * digits: for identity, a wrong match files one person's appointments under
 * another person's name, which is a privacy incident that cannot be undone.
 *
 * The costs here are the other way round. An extra candidate in a list costs
 * nothing — Carrie reads the name and picks the right one. A missing candidate
 * costs her a duplicate client record and a split history. So this is
 * deliberately more generous than `phoneKey`, and deliberately not shared with
 * it: they are answering different questions, and one function doing both
 * would have to be wrong for one of them.
 */
function phoneNeedles(query: string): string[] {
  const d = digits(query);
  const forms = [d];
  // National "0…" against a stored international "44…", and the reverse.
  if (d.startsWith("0")) forms.push(d.slice(1));
  return forms;
}

/** Lower-case, accents stripped, runs of whitespace collapsed. */
function fold(value: string): string {
  return value
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/\s+/g, " ").trim();
}

/**
 * Clients matching what has been typed, best first.
 *
 * Matches a name by WORD PREFIX rather than by substring. Typing "an" should
 * find Anna and Andrew, not everybody with "an" buried in the middle of their
 * surname — a picker that answers a two-letter query with half the list has
 * not helped anybody.
 *
 * A query of digits is treated as a phone number and matched anywhere in the
 * number, because people remember the end of a number ("...900123") at least
 * as often as the start.
 *
 * Ordering, in order:
 *   1. name starts with the query          — what they almost always meant
 *   2. some later word starts with it      — surnames
 *   3. phone match
 *   4. alphabetical, so the list is stable and scannable when nothing is typed
 */
export function matchClients(clients: ClientRecord[], query: string): ClientRecord[] {
  const q = fold(query);
  const byName = (a: ClientRecord, b: ClientRecord) => a.fullName.localeCompare(b.fullName, "en-GB");
  if (q.length === 0) return [...clients].sort(byName);

  const qDigits = digits(query);
  // Two digits is not a phone number, it is somebody starting to type one; at
  // that length it would match nearly every client.
  const asPhone = qDigits.length >= 3 && /^[\d\s()+-]+$/.test(query.trim());

  const scored: { client: ClientRecord; rank: number }[] = [];
  for (const client of clients) {
    const name = fold(client.fullName);
    const words = name.split(" ");

    let rank = -1;
    if (name.startsWith(q)) rank = 0;
    else if (words.some(w => w.startsWith(q))) rank = 1;
    else if (asPhone && client.phone
             && phoneNeedles(query).some(n => digits(client.phone!).includes(n))) rank = 2;

    if (rank >= 0) scored.push({ client, rank });
  }

  return scored
    .sort((a, b) => a.rank - b.rank || byName(a.client, b.client))
    .map(s => s.client);
}

export type NewClient = { fullName: string; phone: string };

export type ClientProblem = { field: "fullName" | "phone"; reason: string };

/**
 * What is wrong with a client somebody is typing in.
 *
 * The phone number is required, and that is a product decision rather than a
 * form convention: it is how a returning client is recognised. A record
 * without one becomes a second record for the same person the next time they
 * ring up, and their history splits in half.
 */
export function validateClient(draft: NewClient): ClientProblem[] {
  const problems: ClientProblem[] = [];
  if (draft.fullName.trim().length < 2) {
    problems.push({ field: "fullName", reason: "Give the client's name." });
  }
  if (digits(draft.phone).length < 7) {
    problems.push({
      field: "phone",
      reason: "A phone number is needed — it is how you will recognise them next time.",
    });
  }
  return problems;
}
